<script setup>
import axios from 'axios';
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { extractApiError } from './apiError';
import { setLocale, t } from './i18n';
import { isPhone } from './isPhone';
import {
  applyGroupSaved, applyImported, busy as portfoliosBusy, editedGroup, error as portfoliosError,
  groupDialogOpen, importDialogOpen, keyConfigured, loadPortfolios, loaded as portfoliosLoaded,
  loading as portfoliosLoading, message as portfoliosMessage, portfolios, refresh, resetPortfolios,
} from './portfolioStore';
import BottomNav from './components/BottomNav.vue';
import GuestLandingPage from './components/GuestLandingPage.vue';
import ImportPortfoliosDialog from './components/ImportPortfoliosDialog.vue';
import PortfolioGroupDialog from './components/PortfolioGroupDialog.vue';
import LockScreen from './components/LockScreen.vue';
import OnboardingScreen from './components/OnboardingScreen.vue';
import LoginForm from './components/LoginForm.vue';
import PortfoliosPage from './components/PortfoliosPage.vue';
import RegisterForm from './components/RegisterForm.vue';
import SettingsPage from './components/SettingsPage.vue';

const currentView = ref('home');
const mode = ref('welcome');
const loading = ref(false);
const message = ref('');
const error = ref('');
const user = ref(null);
// App lock state from the server: { setup_required, locked, has_pin, has_passkey, attempts_left } (docs/pwa/SPEC.md).
const lock = ref(null);
const isLocked = computed(() => Boolean(user.value && lock.value?.locked));
// After a password sign-in: a new PIN, then the Face ID offer (decisions 20–21). The lock comes first.
const needsSetup = computed(() => Boolean(user.value && !isLocked.value && lock.value?.setup_required));
// Pages are not mounted while either screen is up: they neither show nor request data.
const isBlocked = computed(() => isLocked.value || needsSetup.value);
// Amber notice on the login form, e.g. signed out after 5 wrong PINs.
const warning = ref('');
// Until /auth/me answers we don't know if there is a session: show neither the login form nor a page.
const authChecked = ref(false);
const pendingVerificationEmail = ref('');
const registerValidationError = ref('');
const loginValidationError = ref('');
const accountMenuOpen = ref(false);
const accountMenuRef = ref(null);
// Phone chrome (docs/design/SPEC.md, stage M1): the header border shows once the page scrolls,
// «Профиль» in the tab bar gets an amber dot while the API key is missing.
const scrolled = ref(false);
const keyMissing = computed(() => portfoliosLoaded.value && !keyConfigured.value);
const showMobileChrome = computed(() => Boolean(authChecked.value && user.value && !isBlocked.value));

// Portfolio data is shared by the pages and «Профиль» (resources/js/portfolioStore.js): loaded for the
// signed-in user once the app is unlocked, dropped on sign-out.
watch(
  () => (user.value && !isBlocked.value ? user.value.id : null),
  (id) => {
    if (id) {
      loadPortfolios({ silent: portfoliosLoaded.value });
    } else if (!user.value) {
      resetPortfolios();
    }
  },
  { immediate: true }
);

// Settings has no portfolio notice block; report account-menu actions there as toasts too.
watch(portfoliosMessage, (text) => {
  if (text && (isPhone.value || currentView.value === 'settings')) {
    showToast(text);
  }
});
watch(portfoliosError, (text) => {
  if (text && (isPhone.value || currentView.value === 'settings')) {
    showToast(text, 'info', 5000);
  }
});

function onScroll() {
  scrolled.value = window.scrollY > 4;
}

function onNavigate(view) {
  if (view === 'settings') {
    goSettings();
  } else if (view === 'home') {
    goHome();
  } else {
    goSection(view);
  }
}

function openAuth(modeName) {
  mode.value = modeName;
  error.value = '';
  if (modeName === 'login') {
    loginValidationError.value = '';
  } else {
    registerValidationError.value = '';
  }
}

const toastVisible = ref(false);
const toastText = ref('');
const toastVariant = ref('success');
let toastHideTimer = null;
let toastClearTimer = null;

function showToast(text, variant = 'success', durationMs = 3800) {
  toastText.value = text;
  toastVariant.value = variant;
  toastVisible.value = true;
  if (toastHideTimer !== null) {
    clearTimeout(toastHideTimer);
  }
  if (toastClearTimer !== null) {
    clearTimeout(toastClearTimer);
  }
  toastHideTimer = setTimeout(() => {
    toastVisible.value = false;
    toastHideTimer = null;
    toastClearTimer = setTimeout(() => {
      toastText.value = '';
      toastClearTimer = null;
    }, 280);
  }, durationMs);
}

const displayName = computed(() => {
  if (!user.value) {
    return '';
  }
  return user.value.name?.trim() ? user.value.name.trim() : t('app.account');
});

const registerForm = reactive({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
});

const loginForm = reactive({
  email: '',
  password: '',
});

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isRegisterValid = computed(() => {
  return registerForm.name.trim().length > 1
    && emailPattern.test(registerForm.email.trim())
    && registerForm.password.length >= 6
    && registerForm.password_confirmation.length >= 6
    && registerForm.password === registerForm.password_confirmation;
});

const isLoginValid = computed(() => {
  return emailPattern.test(loginForm.email.trim())
    && loginForm.password.length > 0;
});

// The language saved in the profile follows the user across devices (docs/app/SPEC.md, decision 8).
function applyUserLocale(u) {
  if (u?.locale) {
    setLocale(u.locale);
  }
}

// The instrument screen (docs/instrument/SPEC.md, decision 1): /instrument/<uid>.
const INSTRUMENT_PATH = /^\/instrument\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const instrumentUid = ref(null);
// The tab the instrument screen was opened from stays lit (portfolio, markets or news).
const instrumentFrom = ref('home');

function normalizePath(pathname) {
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/settings') {
    return 'settings';
  }
  const instrument = p.match(INSTRUMENT_PATH);
  if (instrument) {
    instrumentUid.value = instrument[1].toLowerCase();
    const from = window.history.state?.from;
    instrumentFrom.value = ['markets', 'news'].includes(from) ? from : 'home';
    return 'instrument';
  }
  return SECTION_PATHS[p] ?? 'home';
}

// Portfolio sections: "/", "/cashflow", "/analytics", "/operations", "/news", "/markets" (docs/cashflow/SPEC.md
// decision 1, docs/analytics/SPEC.md decision 1, docs/operations/SPEC.md decision 2, docs/news/SPEC.md decision 13,
// docs/heatmap/SPEC.md decision 3).
const SECTION_PATHS = {
  '/cashflow': 'cashflow', '/analytics': 'analytics', '/operations': 'operations', '/news': 'news', '/markets': 'markets',
  // docs/achievements/SPEC.md
  '/achievements': 'achievements',
};
// Screens inside the «Портфель» tab, opened from the buttons under the total (docs/design/SPEC.md decision 24).
const NESTED_VIEWS = ['operations', 'cashflow', 'analytics', 'achievements'];

// Desktop section tabs in the header, right of the logo; they mirror the phone tab bar (docs/heatmap/SPEC.md,
// decision 34). Nested screens keep «Портфель» active; «Профиль» lives in the account menu.
const HEADER_TABS = [
  { key: 'portfolio' },
  { key: 'markets' },
  { key: 'strategy', soon: true },
  { key: 'news' },
];
const activeTab = computed(() => {
  if (currentView.value === 'instrument') {
    return instrumentFrom.value === 'home' ? 'portfolio' : instrumentFrom.value;
  }
  if (currentView.value === 'home' || NESTED_VIEWS.includes(currentView.value)) {
    return 'portfolio';
  }
  return HEADER_TABS.some((tab) => tab.key === currentView.value) ? currentView.value : null;
});

function openTab(tab) {
  accountMenuOpen.value = false;
  if (tab.soon) {
    showToast(t('common.inDevelopment'), 'info', 2500);
  } else if (tab.key === 'portfolio') {
    goHome();
  } else {
    goSection(tab.key);
  }
}

// History as in native apps (docs/design/SPEC.md, decision 32): a tab replaces the current entry, so swiping
// back never walks through tabs; a nested screen pushes one. `depth` counts nested steps above the tab's root.
let pendingTab = null;

function historyDepth() {
  return window.history.state?.depth ?? 0;
}

function goTab(path) {
  const depth = historyDepth();
  if (depth > 0) {
    // Unwind the nested screens first, or «back» on the new tab would return to them; popstate finishes.
    pendingTab = path;
    window.history.go(-depth);
    return;
  }
  if (window.location.pathname !== path) {
    window.history.replaceState({ depth: 0 }, '', path);
  }
  syncRouteFromPath();
}

function pushNested(path) {
  if (window.location.pathname === path) {
    return;
  }
  // `from` keeps the tab the screen was opened from lit.
  window.history.pushState({ from: currentView.value, depth: historyDepth() + 1 }, '', path);
  syncRouteFromPath();
}

function onPopState() {
  if (pendingTab !== null) {
    const path = pendingTab;
    pendingTab = null;
    goTab(path);
    return;
  }
  syncRouteFromPath();
}

function goSection(section) {
  const path = Object.keys(SECTION_PATHS).find((p) => SECTION_PATHS[p] === section) ?? '/';
  if (NESTED_VIEWS.includes(section)) {
    pushNested(path);
  } else {
    goTab(path);
  }
}

function openInstrument(uid) {
  pushNested(`/instrument/${uid}`);
  window.scrollTo({ top: 0 });
}

function syncRouteFromPath() {
  currentView.value = normalizePath(window.location.pathname);
}

function goSettings() {
  accountMenuOpen.value = false;
  goTab('/settings');
}

function goHome() {
  accountMenuOpen.value = false;
  goTab('/');
}

function redirectToLoginAfterSessionEnd() {
  user.value = null;
  lock.value = null;
  accountMenuOpen.value = false;
  mode.value = 'login';
  loading.value = false;
  error.value = '';
  if (normalizePath(window.location.pathname) === 'settings') {
    window.history.replaceState({}, document.title, '/');
    syncRouteFromPath();
  }
  message.value = t('auth.sessionEnded');
}

function onSessionEnded() {
  redirectToLoginAfterSessionEnd();
}

function onDocumentClick(e) {
  if (!accountMenuOpen.value) {
    return;
  }
  const el = accountMenuRef.value;
  if (el && !el.contains(e.target)) {
    accountMenuOpen.value = false;
  }
}

async function fetchMe() {
  try {
    const response = await axios.get('/auth/me');
    user.value = response.data.user;
    lock.value = response.data.lock ?? null;
    applyUserLocale(user.value);
  } catch {
    user.value = null;
    lock.value = null;
  }
}

// Any request answered 423: the session got locked on the server (bootstrap.js).
function onAppLocked(e) {
  if (user.value) {
    lock.value = { ...(lock.value ?? {}), ...e.detail };
  }
}

function onOnboardingDone(state) {
  lock.value = state;
}

function onUnlocked(state) {
  lock.value = state ?? { ...lock.value, locked: false };
}

function onLockSignedOut(reason) {
  redirectToLoginAfterSessionEnd();
  message.value = reason === 'session' ? t('auth.sessionEnded') : '';
  warning.value = reason === 'pin_attempts' ? t('lock.tooManyAttempts') : '';
  if (reason === 'manual') {
    showToast(t('auth.loggedOut'));
  }
}

// Back from the background after more than 2 hours → lock (decisions 7, 25). The lock screen goes up
// at once, before the server answers, so the portfolio never flashes.
const BACKGROUND_LOCK_MS = 2 * 60 * 60 * 1000;
let hiddenAt = null;

async function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
  hiddenAt = null;
  if (!user.value || away < BACKGROUND_LOCK_MS) {
    return;
  }
  if (lock.value?.has_pin || lock.value?.has_passkey) {
    lock.value = { ...lock.value, locked: true };
  }
  try {
    const { data } = await axios.post('/app-lock/lock');
    lock.value = data.lock;
  } catch {
    // 401 is handled by the interceptor; offline keeps the optimistic lock.
  }
}

async function submitRegister() {
  registerValidationError.value = '';
  if (!isRegisterValid.value) {
    registerValidationError.value = t('auth.registerInvalid');
    return;
  }

  await submitRegisterRequest(false);
}

async function submitRegisterRequest(forceResend) {
  loading.value = true;
  error.value = '';
  message.value = '';
  registerValidationError.value = '';
  try {
    const response = await axios.post('/auth/register', {
      ...registerForm,
      force_resend: forceResend,
    });
    message.value = response.data.message;
    pendingVerificationEmail.value = registerForm.email;
    loginForm.email = registerForm.email;
    registerForm.name = '';
    registerForm.email = '';
    registerForm.password = '';
    registerForm.password_confirmation = '';
    mode.value = 'login';
  } catch (e) {
    const requiresResendConfirmation = e?.response?.status === 409
      && e?.response?.data?.requires_resend_confirmation;

    if (requiresResendConfirmation) {
      const confirmResend = window.confirm(
        t('auth.confirmResend')
      );
      if (confirmResend) {
        await submitRegisterRequest(true);
      } else {
        message.value = t('auth.usePreviousLink');
      }
      return;
    }

    error.value = extractApiError(e, t('auth.registerFailed'));
  } finally {
    loading.value = false;
  }
}

async function submitLogin() {
  loginValidationError.value = '';
  if (!isLoginValid.value) {
    loginValidationError.value = t('auth.loginInvalid');
    return;
  }

  loading.value = true;
  error.value = '';
  message.value = '';
  try {
    const response = await axios.post('/auth/login', loginForm);
    user.value = response.data.user;
    lock.value = response.data.lock ?? null;
    warning.value = '';
    applyUserLocale(user.value);
    showToast(t('auth.loginSuccess'));
    loginForm.password = '';
  } catch (e) {
    error.value = extractApiError(e, t('auth.loginFailed'));
  } finally {
    loading.value = false;
  }
}

async function submitLogout() {
  accountMenuOpen.value = false;
  loading.value = true;
  error.value = '';
  message.value = '';
  try {
    await axios.post('/auth/logout');
    user.value = null;
    lock.value = null;
    showToast(t('auth.loggedOut'));
  } catch (e) {
    if (e?.response?.status === 401) {
      user.value = null;
      lock.value = null;
      showToast(t('auth.loggedOut'));
    } else {
      error.value = extractApiError(e, t('auth.logoutFailed'));
    }
  } finally {
    loading.value = false;
  }
  if (normalizePath(window.location.pathname) === 'settings') {
    window.history.replaceState({}, document.title, '/');
    syncRouteFromPath();
  }
}

async function resendVerification() {
  if (!pendingVerificationEmail.value) return;
  loading.value = true;
  error.value = '';
  message.value = '';
  try {
    const response = await axios.post('/auth/resend-verification', {
      email: pendingVerificationEmail.value,
    });
    message.value = response.data.message;
  } catch (e) {
    error.value = extractApiError(e, t('auth.resendFailed'));
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  syncRouteFromPath();
  window.addEventListener('popstate', onPopState);
  window.addEventListener('auth:session-ended', onSessionEnded);
  window.addEventListener('app:locked', onAppLocked);
  document.addEventListener('visibilitychange', onVisibilityChange);
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('scroll', onScroll, { passive: true });

  const params = new URLSearchParams(window.location.search);
  const verified = params.get('verified');
  if (verified === '1') {
    message.value = t('auth.verified');
    mode.value = 'login';
    window.history.replaceState({}, document.title, window.location.pathname);
  } else if (verified === 'invalid') {
    error.value = t('auth.verifyInvalid');
    mode.value = 'login';
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  await fetchMe();
  authChecked.value = true;

  if (!user.value && normalizePath(window.location.pathname) === 'settings') {
    window.history.replaceState({}, document.title, '/');
    syncRouteFromPath();
    message.value = t('auth.signInForSettings');
  }
});

onUnmounted(() => {
  if (toastHideTimer !== null) {
    clearTimeout(toastHideTimer);
  }
  if (toastClearTimer !== null) {
    clearTimeout(toastClearTimer);
  }
  window.removeEventListener('popstate', onPopState);
  window.removeEventListener('auth:session-ended', onSessionEnded);
  window.removeEventListener('app:locked', onAppLocked);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  document.removeEventListener('click', onDocumentClick);
  window.removeEventListener('scroll', onScroll);
});
</script>

<template>
  <LockScreen
    v-if="authChecked && isLocked"
    :user="user"
    :lock="lock"
    @unlocked="onUnlocked"
    @signed-out="onLockSignedOut"
  />
  <OnboardingScreen
    v-else-if="authChecked && needsSetup"
    @done="onOnboardingDone"
    @signed-out="onLockSignedOut"
  />

  <main
    class="flex min-h-screen flex-col bg-slate-950 text-slate-100"
    :inert="isBlocked"
  >
    <!-- Phone header: pages put the portfolio pill and the price dot into #mobile-header via Teleport. -->
    <header
      class="sticky top-0 z-40 border-b bg-slate-950/85 pt-[env(safe-area-inset-top)] backdrop-blur-md transition-colors sm:hidden"
      :class="scrolled ? 'border-slate-800/60' : 'border-transparent'"
    >
      <div
        id="mobile-header"
        class="h-11 px-2"
      >
        <p
          v-if="authChecked && !user"
          class="flex h-full items-center justify-center text-[1.0625rem] font-semibold tracking-tight"
        >
          <span class="text-slate-100">Danny</span><span class="text-emerald-400">&nbsp;Top</span>
        </p>
      </div>
    </header>

    <header
      class="sticky top-0 z-40 hidden border-b border-slate-800/80 bg-slate-950/90 pt-[env(safe-area-inset-top)] backdrop-blur-md sm:block"
    >
      <div class="mx-auto flex h-14 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          class="flex shrink-0 items-center gap-2 rounded-md py-0.5 pl-0.5 pr-1 text-left transition hover:bg-slate-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          :aria-label="t('app.homeAria')"
          @click="goHome"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-[0.6875rem] font-bold leading-none tracking-tight text-emerald-400 ring-1 ring-slate-700/80"
            aria-hidden="true"
          >DT</span>
          <span class="text-base font-semibold tracking-tight sm:text-lg">
            <span class="text-slate-100">Danny</span><span class="text-emerald-400"> Top</span>
          </span>
        </button>

        <nav
          v-if="user && !isBlocked"
          class="mr-auto flex gap-1"
          :aria-label="t('sections.label')"
        >
          <button
            v-for="tab in HEADER_TABS"
            :key="tab.key"
            type="button"
            class="whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
            :class="activeTab === tab.key ? 'bg-slate-800 text-slate-100' : tab.soon ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-200'"
            :aria-current="activeTab === tab.key ? 'page' : undefined"
            :aria-disabled="tab.soon ? 'true' : undefined"
            @click="openTab(tab)"
          >
            {{ t(`sections.${tab.key}`) }}
          </button>
        </nav>

        <div
          v-if="user"
          ref="accountMenuRef"
          class="relative flex min-w-0 items-center justify-end"
        >
          <button
            type="button"
            class="flex max-w-full items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/80 px-3 py-1.5 text-sm font-medium text-slate-200 transition hover:border-slate-600 hover:bg-slate-800/80"
            :aria-expanded="accountMenuOpen"
            aria-haspopup="true"
            @click.stop="accountMenuOpen = !accountMenuOpen"
          >
            <span class="truncate">{{ displayName }}</span>
            <svg
              class="h-4 w-4 shrink-0 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-show="accountMenuOpen"
              class="absolute right-0 top-full z-50 mt-1 w-72 origin-top-right rounded-lg border border-slate-700 bg-slate-900 py-1 shadow-xl ring-1 ring-black/20"
              role="menu"
              @click.stop
            >
              <button
                type="button"
                class="block w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800"
                role="menuitem"
                @click="goSettings"
              >
                {{ t('app.settings') }}
              </button>
              <div
                v-if="!portfoliosLoading && keyConfigured"
                class="my-1 border-y border-slate-700 py-1"
                role="group"
              >
                <button
                  v-if="portfolios.length > 0"
                  type="button"
                  class="block min-h-11 w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                  role="menuitem"
                  :disabled="portfoliosBusy"
                  :aria-busy="portfoliosBusy"
                  @click="accountMenuOpen = false; refresh()"
                >
                  {{ portfoliosBusy ? t('portfolios.refreshing') : t('portfolios.refreshAll') }}
                </button>
                <button
                  type="button"
                  class="block min-h-11 w-full px-4 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                  role="menuitem"
                  :disabled="portfoliosBusy"
                  @click="accountMenuOpen = false; importDialogOpen = true"
                >
                  {{ t('portfolios.load') }}
                </button>
              </div>
              <button
                type="button"
                class="block w-full px-4 py-2.5 text-left text-sm text-rose-300 hover:bg-slate-800"
                role="menuitem"
                :disabled="loading"
                @click="submitLogout"
              >
                {{ t('app.logout') }}
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </header>

    <div
      class="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 pt-3 sm:px-6 sm:py-6 lg:px-8"
      :class="showMobileChrome ? 'pb-[calc(52px+env(safe-area-inset-bottom)+24px)]' : 'pb-6'"
    >
      <p
        v-if="!authChecked"
        class="sr-only"
        role="status"
      >
        {{ t('common.loading') }}
      </p>

      <!-- Locked: pages are not mounted, so they neither show data nor request it. -->
      <div
        v-else-if="isBlocked"
        class="flex-1"
      />

      <SettingsPage
        v-else-if="currentView === 'settings'"
        :user="user"
        :logging-out="loading"
        @profile-saved="fetchMe"
        @go-home="goHome"
        @logout="submitLogout"
        @toast="showToast"
      />

      <PortfoliosPage
        v-else-if="user"
        :section="currentView === 'home' ? 'portfolio' : currentView"
        :instrument-uid="instrumentUid"
        @go-settings="goSettings"
        @navigate="goSection"
        @open-instrument="openInstrument"
        @toast="showToast"
      />

      <GuestLandingPage
        v-else-if="mode === 'welcome'"
        @login="openAuth('login')"
        @register="openAuth('register')"
      />

      <div
        v-else
        class="mx-auto w-full max-w-md flex-1"
      >
        <div class="rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-lg">
          <div class="mb-6 flex gap-2">
            <button
              type="button"
              class="rounded-lg px-3 py-1.5 text-sm font-medium transition"
              :class="mode === 'login' ? 'bg-slate-700 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'"
              @click="mode = 'login'"
            >
              {{ t('auth.loginTab') }}
            </button>
            <button
              type="button"
              class="rounded-lg px-3 py-1.5 text-sm font-medium transition"
              :class="mode === 'register' ? 'bg-slate-700 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'"
              @click="mode = 'register'"
            >
              {{ t('auth.registerTab') }}
            </button>
          </div>

          <RegisterForm
            v-if="mode === 'register'"
            v-model:name="registerForm.name"
            v-model:email="registerForm.email"
            v-model:password="registerForm.password"
            v-model:password-confirmation="registerForm.password_confirmation"
            :loading="loading"
            :can-submit="isRegisterValid"
            @submit="submitRegister"
          />

          <LoginForm
            v-else
            v-model:email="loginForm.email"
            v-model:password="loginForm.password"
            :loading="loading"
            :can-submit="isLoginValid"
            @submit="submitLogin"
          />

          <p
            v-if="registerValidationError && mode === 'register'"
            class="mt-4 text-sm text-amber-400"
          >
            {{ registerValidationError }}
          </p>
          <p
            v-if="loginValidationError && mode === 'login'"
            class="mt-4 text-sm text-amber-400"
          >
            {{ loginValidationError }}
          </p>
          <p
            v-if="warning && mode === 'login'"
            class="mt-4 text-sm text-amber-400"
          >
            {{ warning }}
          </p>
          <p
            v-if="message"
            class="mt-4 text-sm text-emerald-400"
          >
            {{ message }}
          </p>
          <p
            v-if="error"
            class="mt-2 text-sm text-rose-400"
          >
            {{ error }}
          </p>
          <button
            v-if="!user && pendingVerificationEmail"
            type="button"
            class="mt-4 rounded-lg bg-amber-600 px-3 py-2 text-sm font-medium hover:bg-amber-500"
            :disabled="loading"
            @click="resendVerification"
          >
            {{ t('auth.resend') }}
          </button>
        </div>
      </div>

      <div
        v-if="user && (message || error)"
        class="mt-4"
      >
        <p
          v-if="message"
          class="text-sm text-emerald-400"
        >
          {{ message }}
        </p>
        <p
          v-if="error"
          class="text-sm text-rose-400"
        >
          {{ error }}
        </p>
      </div>
    </div>

    <ImportPortfoliosDialog
      v-if="user"
      :open="importDialogOpen"
      @close="importDialogOpen = false"
      @imported="applyImported"
    />
    <PortfolioGroupDialog
      v-if="user"
      :open="groupDialogOpen"
      :portfolios="portfolios"
      :group="editedGroup"
      @close="groupDialogOpen = false"
      @saved="applyGroupSaved"
    />

    <BottomNav
      v-if="showMobileChrome"
      :current="currentView === 'instrument' ? instrumentFrom : NESTED_VIEWS.includes(currentView) ? 'home' : currentView"
      :nested="currentView === 'instrument' || NESTED_VIEWS.includes(currentView)"
      :profile-alert="keyMissing"
      @navigate="onNavigate"
      @soon="(text) => showToast(text, 'info', 2500)"
    />

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="translate-x-full opacity-0"
        enter-to-class="translate-x-0 opacity-100"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="translate-x-0 opacity-100"
        leave-to-class="translate-x-full opacity-0"
      >
        <div
          v-if="toastVisible && toastText"
          class="pointer-events-none fixed left-4 right-4 top-[calc(3.5rem+env(safe-area-inset-top))] z-[100] flex flex-col gap-2 sm:left-auto sm:right-6 sm:top-24 sm:max-w-sm"
          role="status"
          aria-live="polite"
        >
          <div
            class="pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-2xl ring-1 ring-black/30 backdrop-blur-sm"
            :class="toastVariant === 'success'
              ? 'border-emerald-500/35 bg-slate-900/95 text-emerald-50'
              : 'border-slate-600/60 bg-slate-900/95 text-slate-100'"
          >
            <span
              v-if="toastVariant === 'success'"
              class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400"
              aria-hidden="true"
            >
              <svg
                class="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </span>
            <p class="min-w-0 flex-1 text-sm font-medium leading-snug">
              {{ toastText }}
            </p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </main>
</template>
